import type { ChangeEvent } from "react";
import { tokens } from "../../design-system/tokens";

type CheckboxColor = "default" | "onPrimary";

type CheckboxProps = {
  name: string;
  /** Omit or pass empty string for checkbox-only (e.g. table selection). */
  title?: string;
  checked: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  size?: "small" | "medium" | "large";
  /** Use onPrimary on dark headers (e.g. table thead). */
  color?: CheckboxColor;
  semiSelected?: boolean;
  className?: string;
  disabled?: boolean;
  ariaLabel?: string;
};

const colorStyles: Record<
  CheckboxColor,
  {
    label: string;
    border: string;
    borderChecked: string;
    backgroundChecked: string;
    checkmark: string;
  }
> = {
  default: {
    label: tokens.colors.text.primary,
    border: tokens.colors.inputBorder,
    borderChecked: tokens.colors.secondary,
    backgroundChecked: tokens.colors.secondary,
    checkmark: "white",
  },
  onPrimary: {
    label: tokens.colors.text.inverse,
    border: tokens.colors.text.inverse,
    borderChecked: tokens.colors.secondary,
    backgroundChecked: tokens.colors.secondary,
    checkmark: tokens.colors.primary,
  },
};

export default function Checkbox({
  name,
  title = "",
  checked,
  onChange,
  size = "medium",
  color = "default",
  semiSelected = false,
  className,
  disabled = false,
  ariaLabel,
}: CheckboxProps) {
  const showTitle = Boolean(title?.trim());
  const palette = colorStyles[color];
  const checkSvg = encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 10'><path fill='${palette.checkmark}' d='M4.3 9.2.4 5.3l1.4-1.4 2.5 2.5L10.2.5l1.4 1.4z'/></svg>`
  );
  const sizeConfig = {
    small: {
      checkboxSize: "16px",
      borderRadius: tokens.borderRadius.sm,
      typography: tokens.typography.inputSmall,
      gap: tokens.spacing[1],
    },
    medium: {
      checkboxSize: "18px",
      borderRadius: "4px",
      typography: tokens.typography.inputMedium,
      gap: tokens.spacing[2],
    },
    large: {
      checkboxSize: "20px",
      borderRadius: "4px",
      typography: tokens.typography.inputLarge,
      gap: tokens.spacing[2],
    },
  } as const;
  const currentSize = sizeConfig[size];

  return (
    <label
      className={className}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: showTitle ? "flex-start" : "center",
        flexDirection: "row",
        gap: showTitle ? currentSize.gap : 0,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: semiSelected ? 0.5 : 1,
      }}
    >
      {showTitle ? (
        <span
          style={{
            fontFamily: currentSize.typography.fontFamily,
            fontSize: currentSize.typography.fontSize,
            fontWeight: currentSize.typography.fontWeight,
            lineHeight: String(currentSize.typography.lineHeight),
            color: palette.label,
          }}
        >
          {title}
        </span>
      ) : null}

      <input
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        aria-label={ariaLabel}
        style={{
          appearance: "none",
          WebkitAppearance: "none",
          boxSizing: "border-box",
          flexShrink: 0,
          width: currentSize.checkboxSize,
          height: currentSize.checkboxSize,
          minWidth: currentSize.checkboxSize,
          minHeight: currentSize.checkboxSize,
          aspectRatio: "1 / 1",
          borderRadius: currentSize.borderRadius,
          border: `1px solid ${checked ? palette.borderChecked : palette.border}`,
          backgroundColor: checked ? palette.backgroundChecked : "transparent",
          backgroundImage: checked
            ? `url("data:image/svg+xml,${checkSvg}")`
            : "none",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundSize: "70%",
          margin: 0,
          padding: 0,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      />
    </label>
  );
}
